import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

/**
 * Review and apply the changes an audit proposed.
 *
 * Three tracks, shown as three separate asks rather than one list, because
 * they are addressed to different people:
 *
 *   We can do this    one click each, written to the site, undoable
 *   Ready for you     we wrote the exact value; someone pastes it
 *   Needs a person    judgement, off-site or commercial work
 *
 * Only the first track has approve controls. Offering them on the others would
 * misrepresent what this system does — nothing acts on an approved assisted
 * item, so a checkbox there would be a lie told by the interface.
 */

const SEVERITY = {
  critical: ["#fee2e2", "#991b1b"],
  high: ["#ffedd5", "#9a3412"],
  medium: ["#fef9c3", "#854d0e"],
  low: ["#f3f4f6", "#4b5563"],
};

const OWNER_LABEL = {
  developer: "Developer",
  editor: "Whoever edits the site",
  "developer+seo": "Developer with SEO",
  marketer: "Marketing",
  brand_owner: "Brand owner",
  business_owner: "Business owner",
  devops: "Hosting / DevOps",
  user: "You",
};

const S = {
  card: { border: "1px solid #e5e7eb", borderRadius: 10, background: "#fff", marginBottom: 12 },
  head: { padding: "14px 18px", borderBottom: "1px solid #f3f4f6" },
  body: { padding: "14px 18px" },
  h: { margin: 0, fontSize: 15, fontWeight: 600 },
  sub: { margin: "4px 0 0", fontSize: 13, color: "#6b7280", lineHeight: 1.5 },
  row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  btn: {
    padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db",
    background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 500,
  },
  btnPrimary: {
    padding: "8px 16px", borderRadius: 6, border: "1px solid #1f2937",
    background: "#1f2937", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
  },
  pill: (bg, fg) => ({
    padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
    background: bg, color: fg, textTransform: "uppercase", letterSpacing: 0.4,
  }),
  url: {
    fontSize: 11, color: "#6b7280",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  diff: {
    marginTop: 10, borderRadius: 6, overflow: "hidden", border: "1px solid #e5e7eb",
    fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  diffRow: (bg) => ({
    padding: "8px 10px", background: bg, display: "flex", gap: 8,
    whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5,
  }),
  marker: { width: 12, flexShrink: 0, fontWeight: 700, opacity: 0.6 },
  banner: (bg, border, fg) => ({
    padding: "14px 18px", borderRadius: 10, border: `1px solid ${border}`,
    background: bg, color: fg, fontSize: 13, marginBottom: 16, lineHeight: 1.6,
  }),
  tab: (active) => ({
    padding: "8px 14px", borderRadius: 8, border: "1px solid",
    borderColor: active ? "#1f2937" : "#e5e7eb",
    background: active ? "#1f2937" : "#fff",
    color: active ? "#fff" : "#374151",
    cursor: "pointer", fontSize: 13, fontWeight: 500,
  }),
};

function Diff({ before, after }) {
  const hasBefore = before !== null && before !== undefined && String(before).trim() !== "";
  return (
    <div style={S.diff}>
      <div style={S.diffRow(hasBefore ? "#fef2f2" : "#f9fafb")}>
        <span style={S.marker}>{hasBefore ? "−" : " "}</span>
        <span style={{ color: hasBefore ? "#991b1b" : "#9ca3af" }}>
          {hasBefore ? String(before) : "(nothing there at the moment)"}
        </span>
      </div>
      <div style={S.diffRow("#f0fdf4")}>
        <span style={S.marker}>+</span>
        <span style={{ color: "#166534" }}>{String(after)}</span>
      </div>
    </div>
  );
}

function CopyButton({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      style={S.btn}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1800);
        } catch {
          toast.error("Could not copy — select the text and copy it manually");
        }
      }}
    >
      {done ? "Copied" : label}
    </button>
  );
}

export default function ChangesetReview({ executionId }) {
  const [data, setData] = useState(null);
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [track, setTrack] = useState("auto");
  const [busy, setBusy] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.getChangeset(executionId);
      setData(res.changeset);
      setConnection(res.connection);
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error(err.response?.data?.error || "Could not load the proposed changes");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => data?.tracks?.[track] || [], [data, track]);

  const approvedCount = useMemo(
    () => (data?.tracks?.auto || []).filter((i) => i.status === "approved").length,
    [data],
  );

  const decide = async (itemId, decision) => {
    setBusy(itemId);
    try {
      await api.decideChangeItem(executionId, itemId, decision);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not record that");
    } finally {
      setBusy(null);
    }
  };

  const approveAll = async () => {
    setBusy("all");
    try {
      const { data: res } = await api.approveAllChanges(executionId);
      toast.success(`${res.approved} change${res.approved === 1 ? "" : "s"} approved`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not approve those");
    } finally {
      setBusy(null);
    }
  };

  /**
   * The one button in this system that changes a live website.
   *
   * Confirmed with the count and the domain spelled out — not a generic "are
   * you sure". The person clicking this is usually doing it on a client's site,
   * and the thing they need to check is how many pages are about to change.
   */
  const apply = async () => {
    const n = approvedCount;
    if (
      !window.confirm(
        `Apply ${n} approved change${n === 1 ? "" : "s"} to ${data.domain}?\n\n` +
          "These are written to the live site now. Each one can be undone from this screen afterwards.",
      )
    ) {
      return;
    }
    setBusy("apply");
    setApplyResult(null);
    try {
      const { data: res } = await api.applyChangeset(executionId);
      setApplyResult(res);
      const s = res.summary;
      if (s.failed || s.refused) {
        toast.warn(`${s.applied} applied, ${s.failed + s.refused} did not go through`);
      } else {
        toast.success(`${s.applied + s.unchanged} change${s.applied + s.unchanged === 1 ? "" : "s"} applied`);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not apply those changes");
    } finally {
      setBusy(null);
    }
  };

  const revert = async (itemId, title) => {
    if (!window.confirm(`Undo "${title}"?\n\nThe previous value will be put back on the live site.`)) {
      return;
    }
    setBusy(itemId);
    try {
      await api.revertChangeItem(executionId, itemId);
      toast.success("Undone");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not undo that");
    } finally {
      setBusy(null);
    }
  };

  // Nothing to say until there is something to review. Most runs produce no
  // changeset at all, and this sits in the run timeline, so an "it didn't"
  // card on every completed run would be noise rather than information.
  if (loading || !data) return null;

  const canApply = connection?.writesEnabled && ["verified", "degraded"].includes(connection?.status);

  return (
    <div>
      {/* --- why the automatic track may be empty ----------------------- */}
      {!data.connected && data.blockedByAccess > 0 && (
        <div style={S.banner("#eff6ff", "#bfdbfe", "#1e3a8a")}>
          <strong>
            Connect {data.domain} and {data.blockedByAccess} of these become one-click changes.
          </strong>
          <br />
          Nothing is connected yet, so every change below is written out for someone to apply by
          hand. Connecting the site does not change anything on it — you still approve each change
          individually.
        </div>
      )}

      {data.connected && !connection?.writesEnabled && (
        <div style={S.banner("#fffbeb", "#fde68a", "#92400e")}>
          <strong>Review only.</strong> The site is connected but writes are switched off, so
          nothing can be applied yet. Turn them on in the project's website settings.
        </div>
      )}

      {/* --- tracks ----------------------------------------------------- */}
      <div style={{ ...S.row, marginBottom: 14 }}>
        <button style={S.tab(track === "auto")} onClick={() => setTrack("auto")}>
          We can do this · {data.counts.auto}
        </button>
        <button style={S.tab(track === "assisted")} onClick={() => setTrack("assisted")}>
          Ready for you · {data.counts.assisted}
        </button>
        <button style={S.tab(track === "human")} onClick={() => setTrack("human")}>
          Needs a person · {data.counts.human}
        </button>
      </div>

      {/* --- apply bar -------------------------------------------------- */}
      {track === "auto" && data.counts.auto > 0 && (
        <div style={{ ...S.card, ...S.body, ...S.row, justifyContent: "space-between" }}>
          <span style={{ fontSize: 13 }}>
            {approvedCount > 0 ? (
              <>
                <strong>{approvedCount}</strong> approved, ready to apply
              </>
            ) : (
              "Approve the changes you want, then apply them."
            )}
          </span>
          <div style={S.row}>
            <button style={S.btn} onClick={approveAll} disabled={busy === "all"}>
              Approve all
            </button>
            <button
              style={{ ...S.btnPrimary, opacity: approvedCount && canApply ? 1 : 0.45 }}
              onClick={apply}
              disabled={!approvedCount || !canApply || busy === "apply"}
              title={!canApply ? "Connect the site and allow changes first" : undefined}
            >
              {busy === "apply" ? "Applying…" : `Apply to ${data.domain}`}
            </button>
          </div>
        </div>
      )}

      {/* --- apply results ---------------------------------------------- */}
      {applyResult && (
        <div style={S.banner("#f9fafb", "#e5e7eb", "#111827")}>
          <strong>
            {applyResult.summary.applied} applied · {applyResult.summary.unchanged} already correct ·{" "}
            {applyResult.summary.unverified} not yet visible · {applyResult.summary.failed} failed ·{" "}
            {applyResult.summary.refused} refused
          </strong>
          {applyResult.results
            ?.filter((r) => r.detail)
            .map((r) => (
              <div key={r.id} style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                <strong>{r.title}</strong> — {r.detail}
              </div>
            ))}
        </div>
      )}

      {/* --- the items -------------------------------------------------- */}
      {items.length === 0 && (
        <div style={{ ...S.card, ...S.body }}>
          <p style={{ ...S.sub, margin: 0 }}>
            {track === "auto"
              ? "Nothing here can be applied automatically for this page."
              : track === "assisted"
                ? "Nothing is waiting for you to apply by hand."
                : "Nothing here needs a person."}
          </p>
        </div>
      )}

      {items.map((item) => {
        const [sbg, sfg] = SEVERITY[item.severity] || SEVERITY.low;
        return (
          <div key={item.id} style={S.card}>
            <div style={S.head}>
              <div style={{ ...S.row, justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ ...S.row, gap: 8 }}>
                    <span style={S.pill(sbg, sfg)}>{item.severity}</span>
                    {item.reason && (
                      <span style={S.pill("#ede9fe", "#5b21b6")}>{item.reason.label}</span>
                    )}
                    {item.status === "applied" && <span style={S.pill("#dcfce7", "#166534")}>applied</span>}
                    {item.status === "unverified" && (
                      <span style={S.pill("#fef9c3", "#854d0e")}>not visible yet</span>
                    )}
                    {item.status === "reverted" && <span style={S.pill("#f3f4f6", "#4b5563")}>undone</span>}
                    {item.status === "failed" && <span style={S.pill("#fee2e2", "#991b1b")}>failed</span>}
                    {item.status === "approved" && <span style={S.pill("#dbeafe", "#1e40af")}>approved</span>}
                    {item.status === "rejected" && <span style={S.pill("#f3f4f6", "#6b7280")}>skipped</span>}
                  </div>
                  <h4 style={{ ...S.h, marginTop: 8 }}>{item.title}</h4>
                  {item.target?.url && <div style={{ ...S.url, marginTop: 4 }}>{item.target.url}</div>}
                </div>

                {/* Approve controls exist only on the automatic track. */}
                {track === "auto" && (
                  <div style={S.row}>
                    {["applied", "unverified"].includes(item.status) ? (
                      <button
                        style={S.btn}
                        disabled={busy === item.id}
                        onClick={() => revert(item.id, item.title)}
                      >
                        Undo
                      </button>
                    ) : item.status === "reverted" ? null : (
                      <>
                        <button
                          style={{
                            ...S.btn,
                            ...(item.status === "approved"
                              ? { borderColor: "#1e40af", color: "#1e40af", background: "#eff6ff" }
                              : {}),
                          }}
                          disabled={busy === item.id}
                          onClick={() => decide(item.id, item.status === "approved" ? "reset" : "approve")}
                        >
                          {item.status === "approved" ? "Approved" : "Approve"}
                        </button>
                        <button
                          style={S.btn}
                          disabled={busy === item.id}
                          onClick={() => decide(item.id, item.status === "rejected" ? "reset" : "reject")}
                        >
                          {item.status === "rejected" ? "Skipped" : "Skip"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div style={S.body}>
              <p style={{ ...S.sub, marginTop: 0 }}>{item.why}</p>

              {item.reason?.blurb && (
                <p style={{ ...S.sub, marginTop: 8 }}>
                  <strong>{OWNER_LABEL[item.reason.owner] || "Someone"}:</strong> {item.reason.blurb}
                </p>
              )}

              {/* Why an agent's proposal was downgraded. Shown rather than
                  hidden: "we would have done this for you, but…" is the most
                  actionable sentence on the screen. */}
              {item.downgradeWhy && (
                <p style={{ ...S.sub, marginTop: 8, color: "#92400e" }}>{item.downgradeWhy}</p>
              )}

              {item.after !== null && item.after !== undefined && (
                <>
                  <Diff before={item.before} after={item.after} />
                  {track !== "auto" && (
                    <div style={{ ...S.row, marginTop: 8 }}>
                      <CopyButton text={String(item.after)} label="Copy the new value" />
                      {item.field && <span style={S.url}>field: {item.field}</span>}
                    </div>
                  )}
                </>
              )}

              {item.artifact && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ ...S.row, justifyContent: "space-between", marginBottom: 6 }}>
                    <strong style={{ fontSize: 12 }}>Ready to paste in</strong>
                    <CopyButton text={item.artifact} />
                  </div>
                  <pre
                    style={{
                      margin: 0, padding: 12, background: "#f9fafb", borderRadius: 6,
                      border: "1px solid #e5e7eb", fontSize: 11, overflowX: "auto",
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}
                  >
                    {item.artifact}
                  </pre>
                </div>
              )}

              {item.evidence && (
                <p style={{ ...S.sub, marginTop: 10, fontSize: 12 }}>
                  <strong>What we found:</strong> {item.evidence}
                </p>
              )}

              {item.applyError && (
                <p style={{ ...S.sub, marginTop: 8, color: "#b91c1c" }}>{item.applyError}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
